"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

/**
 * Landing PageView — dual setup. On each route, fires the browser Pixel
 * PageView and the server-side Conversions API PageView with the SAME
 * `eventId`, so Meta deduplicates (recovers events ad blockers / ITP drop).
 *
 * `<FacebookPixel />` only renders `fbq('init', …)` (no PageView), so this is
 * the single PageView source for JS-enabled visitors. If `window.fbq` is
 * absent the Pixel didn't render (opted out / no id) — we skip BOTH the
 * browser event and the server call, keeping them in lock-step with consent.
 *
 * Keyed on `usePathname()` (not search params) so it captures client
 * navigations without needing a Suspense boundary.
 */
export function FacebookPixelPageView() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;
    const fbq = (window as unknown as { fbq?: (...a: unknown[]) => void }).fbq;
    if (!fbq) return; // Pixel not active (opted out / not configured).

    const eventId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `pv-${Date.now()}-${Math.round(Math.random() * 1e9)}`;

    fbq("track", "PageView", {}, { eventID: eventId });

    try {
      void fetch("/api/conversions/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          eventName: "PageView",
          eventId,
          eventSourceUrl: window.location.href,
        }),
      });
    } catch {
      /* ignore */
    }
  }, [pathname]);

  return null;
}

export default FacebookPixelPageView;
